import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LanguageIcon from "@mui/icons-material/Language";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import GitHubIcon from "@mui/icons-material/GitHub";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import { useThemeColors } from "../context/ThemeContext";
import FlatCard from "../components/FlatCard";
import api from "../api";
import {
  useDomain,
  useActivityLog,
  useTemplates,
  useSubdomains,
  useDeleteSubdomain,
  useGithubStatus,
  useCreateGithubRepo,
  useUploadZipToRepo,
  useGithubUploadProgress,
} from "../hooks/queries";
import { renderTemplate } from "../utils/messageTemplate";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import AddSubdomainModal from "../components/AddSubdomainModal";
import { ListSkeleton } from "../components/TableSkeleton";
import { HEADING_FONT } from "../theme/fonts";

function computeStatus(expiryDate) {
  if (!expiryDate) return "Active";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(expiryDate) >= today ? "Active" : "Expired";
}

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const then = new Date(dateStr.replace(" ", "T"));
  if (isNaN(then.getTime())) return dateStr;
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo ago`;
}

function formatAbsolute(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatClock(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function groupActivityByDay(entries) {
  const groups = new Map();
  for (const e of entries) {
    if (!e.createdAt) continue;
    const key = e.createdAt.slice(0, 10);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  return [...groups.entries()].map(([date, items]) => ({ date, items }));
}

function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(date) - today) / (1000 * 60 * 60 * 24));
}

function SectionHeader({ icon, title, colors }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          background:
            "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)",
          color: "#3B82F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            letterSpacing: "-0.2px",
            lineHeight: 1.2,
            mt: "2px",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  );
}

function InfoField({ label, value, icon, mono, colors, copyable }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!value || value === "—") return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: colors.textFaint,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon && (
          <Box sx={{ color: colors.textMuted, display: "flex" }}>{icon}</Box>
        )}
        <Typography
          sx={{
            fontSize: 14,
            color: value && value !== "—" ? colors.text : colors.textFaint,
            fontWeight: 500,
            fontFamily: mono
              ? "ui-monospace, SFMono-Regular, Menlo, monospace"
              : "inherit",
            wordBreak: "break-word",
          }}
        >
          {value || "—"}
        </Typography>
        {copyable && value && value !== "—" && (
          <Tooltip title={copied ? "Copied!" : "Copy"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: colors.textMuted,
                p: 0.25,
                "&:hover": { color: "#3B82F6" },
              }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

function DomainDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);
  const colors = useThemeColors();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [newActivityText, setNewActivityText] = useState("");
  const [addSubOpen, setAddSubOpen] = useState(false);

  // Sub-domains belonging to THIS domain (inherits its registration data).
  const subdomainsQuery = useSubdomains({ domainId: id });
  const subdomains = subdomainsQuery.data || [];
  const deleteSubdomain = useDeleteSubdomain();

  async function handleDeleteSubdomain(sub) {
    const ok = await confirm({
      title: "Delete sub-domain?",
      message: `"${sub.name}" will be permanently removed.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteSubdomain.mutateAsync(sub.id);
      toast.info("Sub-domain deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete sub-domain");
    }
  }

  function handleSubdomainAdded() {
    setAddSubOpen(false);
    queryClient.invalidateQueries({ queryKey: ["subdomains"] });
    toast.success("Sub-domain added successfully");
  }

  // Client-reminder send state. The editable preview is pre-filled from the
  // saved template (rendered with this domain's data) and can be tweaked once
  // before sending over WhatsApp / email.
  const [sendChannel, setSendChannel] = useState("whatsapp");
  const [reminderText, setReminderText] = useState("");
  const [reminderSubject, setReminderSubject] = useState("");

  // The domain + its activity feed come from shared, cached React Query hooks.
  const domainQuery = useDomain(id);
  const domain = domainQuery.data || null;

  const githubStatus = useGithubStatus().data;
  const createGithubRepo = useCreateGithubRepo();
  const uploadZip = useUploadZipToRepo();
  // Only polls while an upload is actually running.
  const uploadProgress = useGithubUploadProgress(id, uploadZip.isPending).data;

  async function handleCreateGithubRepo() {
    try {
      await createGithubRepo.mutateAsync(id);
      toast.success("GitHub repo created");
    } catch (err) {
      toast.error(err.message || "Could not create the GitHub repo");
    }
  }

  // ZIP → repo. The picked File is sent straight to the API and never stored;
  // the repo's files are replaced by the archive's contents in one commit.
  async function handleZipPicked(event) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;
    // Reject an oversized ZIP before uploading it for minutes on end.
    const cap = githubStatus?.hardLimitMb || githubStatus?.zipLimitMb;
    if (cap && file.size > cap * 1048576) {
      toast.error(
        `"${file.name}" is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${cap} MB — split it, or leave large media out of the archive.`
      );
      return;
    }
    try {
      const res = await uploadZip.mutateAsync({ id, file });
      toast.success(`${res.fileCount} file${res.fileCount === 1 ? "" : "s"} uploaded to the repo`);
    } catch (err) {
      toast.error(err.message || "ZIP upload failed");
    }
  }
  // A 404 from the API means the domain doesn't exist (or was deleted).
  const notFound = domainQuery.isError && domainQuery.error?.status === 404;

  const activityQuery = useActivityLog({ domainId: id });
  const activityEntries = activityQuery.data || [];

  // Reminder message templates (shared/cached, edited on the Settings page).
  const templatesQuery = useTemplates();
  const templates = templatesQuery.data;

  // (Re)fill the editable preview from the selected channel's template whenever
  // the dialog opens, the channel switches, or the templates/domain load. Any
  // manual edits the admin makes survive because none of these deps change while
  // they type.
  useEffect(() => {
    if (!addActivityOpen || !templates || !domain) return;
    const body =
      sendChannel === "whatsapp"
        ? templates.whatsappTemplate
        : templates.emailTemplate;
    setReminderText(renderTemplate(body, domain));
    setReminderSubject(renderTemplate(templates.emailSubject, domain));
  }, [addActivityOpen, sendChannel, templates, domain]);

  // Reflect the current domain in the document title (and handle the
  // not-found case), mirroring the old fetch side-effect.
  useEffect(() => {
    if (domain) {
      document.title = `${domain.name || "Domain"} · RABS`;
    } else if (notFound) {
      document.title = "Domain not found · RABS";
    }
  }, [domain, notFound]);

  // Surface non-404 load failures as a toast (matches the old behaviour).
  useEffect(() => {
    if (domainQuery.isError && domainQuery.error?.status !== 404) {
      toast.error(domainQuery.error?.message || "Failed to load domain");
    }
  }, [domainQuery.isError, domainQuery.error, toast]);

  useEffect(() => {
    if (activityQuery.isError) {
      toast.error(activityQuery.error?.message || "Failed to load activity");
    }
  }, [activityQuery.isError, activityQuery.error, toast]);

  async function handleAddActivity() {
    const text = newActivityText.trim();
    if (!text) return;
    try {
      await api.post("/activity-log", {
        domainId: domain.id,
        activityText: text,
      });
      queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      setNewActivityText("");
      setAddActivityOpen(false);
      toast.success("Activity added");
    } catch (err) {
      toast.error(err.message || "Failed to add activity");
    }
  }

  // Send the (editable) reminder message straight to the client over WhatsApp
  // or email, then log it to the activity timeline so we keep a record.
  // Link-based: opens wa.me / mailto prefilled — the admin just hits send.
  async function handleSendToClient(channel) {
    const text = (reminderText || "").trim();
    if (!text) return;

    let url;
    if (channel === "whatsapp") {
      const digits = (domain.clientPhone || "").replace(/\D/g, "");
      if (!digits) {
        toast.error("Client phone number not available");
        return;
      }
      // Indian numbers stored as 10 digits — prepend country code for wa.me.
      const wa = digits.length === 10 ? `91${digits}` : digits;
      url = `https://wa.me/${wa}?text=${encodeURIComponent(text)}`;
    } else {
      const email = (domain.clientEmail || "").trim();
      if (!email) {
        toast.error("Client email not available");
        return;
      }
      const subject =
        (reminderSubject || "").trim() ||
        `Regarding your domain ${domain.name}`;
      url = `mailto:${email}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(text)}`;
    }

    // Log to the timeline (best-effort — never block the send on this).
    const channelLabel = channel === "whatsapp" ? "WhatsApp" : "Email";
    try {
      await api.post("/activity-log", {
        domainId: domain.id,
        activityText: `Sent ${channelLabel} reminder to client:\n${text}`,
      });
      queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    } catch (err) {
      // Swallow — the message is what matters; logging is secondary.
    }

    if (channel === "whatsapp") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
    setAddActivityOpen(false);
    toast.success(
      channel === "whatsapp" ? "Opening WhatsApp…" : "Opening email…"
    );
  }

  if (notFound) {
    return (
      <DashboardLayout>
        <FlatCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#DC2626",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
            }}
          >
            <ErrorOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            Domain not found
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: colors.textMuted, mb: 2 }}
          >
            The domain you're looking for doesn't exist or was deleted.
          </Typography>
          <Button
            onClick={() => navigate("/domains")}
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              background:
                "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
              px: 2.5,
              py: 1,
              borderRadius: 2,
              "&:hover": {
                boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
              },
            }}
          >
            Back to Domains
          </Button>
        </FlatCard>
      </DashboardLayout>
    );
  }

  if (!domain)
    return (
      <DashboardLayout>
        <ListSkeleton rows={6} />
      </DashboardLayout>
    );

  const status = computeStatus(domain.expiry);
  const days = daysUntil(domain.expiry);
  const isActive = status === "Active";
  const urgent = isActive && days !== null && days < 30;
  const statusColor = !isActive
    ? "#DC2626"
    : urgent
    ? "#D97706"
    : "#16A34A";
  const statusBg = !isActive
    ? "rgba(239, 68, 68, 0.12)"
    : urgent
    ? "rgba(245, 158, 11, 0.12)"
    : "rgba(16, 185, 129, 0.12)";

  const externalUrl = domain.name?.startsWith("http")
    ? domain.name
    : `https://${domain.name}`;

  const cardSx = {
    background: "#ffffff",
    border: "1px solid #EEF1F6",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
    p: 3,
  };

  // Pull activity entries for this specific domain, newest first
  const domainActivity = activityEntries
    .filter((a) => String(a.domainId) === String(domain.id))
    .sort((a, b) => {
      const da = a.createdAt
        ? new Date(a.createdAt.replace(" ", "T")).getTime()
        : 0;
      const db = b.createdAt
        ? new Date(b.createdAt.replace(" ", "T")).getTime()
        : 0;
      return db - da;
    });
  const activityDays = groupActivityByDay(domainActivity);

  return (
    <DashboardLayout>
      {/* Back link */}
      <Button
        onClick={() => navigate("/domains")}
        startIcon={<ArrowBackIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: colors.textMuted,
          mb: 2,
          ml: -1,
          "&:hover": {
            background: colors.hover,
            color: colors.text,
          },
        }}
      >
        Back to Domains
      </Button>

      {/* Hero card */}
      <Box
        sx={{
          position: "relative",
          background:
            "linear-gradient(135deg, #3B82F6 0%, #3B82F6 50%, #60A5FA 100%)",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          overflow: "hidden",
          color: "#fff",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LanguageIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  opacity: 0.85,
                }}
              >
                Domain
              </Typography>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 700,
                  letterSpacing: "-0.5px",
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {domain.name || "(unnamed)"}
              </Typography>
              {domain.registrar && (
                <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
                  Registered via {domain.registrar}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              icon={
                isActive ? (
                  <CheckCircleOutlinedIcon
                    sx={{ fontSize: 16, color: "#fff !important" }}
                  />
                ) : (
                  <ErrorOutlinedIcon
                    sx={{ fontSize: 16, color: "#fff !important" }}
                  />
                )
              }
              label={status}
              sx={{
                fontWeight: 700,
                fontSize: 12,
                color: "#fff",
                background: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            />
            <Chip
              component="a"
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              icon={
                <OpenInNewIcon
                  sx={{ fontSize: 16, color: "#3B82F6 !important" }}
                />
              }
              label="Visit Site"
              sx={{
                fontWeight: 700,
                fontSize: 12,
                color: "#3B82F6",
                background: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                textDecoration: "none",
                "&:hover": { background: "#f3f4f6" },
              }}
            />
            {domain.githubRepoUrl ? (
              <Chip
                component="a"
                href={domain.githubRepoUrl}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                icon={<GitHubIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
                label="GitHub Repo"
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#fff",
                  background: "#24292f",
                  border: "1px solid rgba(255,255,255,0.3)",
                  textDecoration: "none",
                  "&:hover": { background: "#32383f" },
                }}
              />
            ) : (
              githubStatus?.enabled &&
              hasPermission("domains", "edit") && (
                <Chip
                  clickable
                  onClick={handleCreateGithubRepo}
                  disabled={createGithubRepo.isPending}
                  icon={<GitHubIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
                  label={
                    createGithubRepo.isPending
                      ? "Creating repo…"
                      : domain.githubRepoStatus === "failed"
                        ? "Retry GitHub Repo"
                        : "Create GitHub Repo"
                  }
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#fff",
                    background:
                      domain.githubRepoStatus === "failed" ? "#b45309" : "rgba(255,255,255,0.22)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              )
            )}
            {githubStatus?.enabled && hasPermission("domains", "edit") && (
              // The label wraps the hidden input so clicking the chip opens the
              // file picker (MUI's Chip renders no children of its own).
              <Box component="label" sx={{ display: "inline-flex", cursor: "pointer" }}>
                <input
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  disabled={uploadZip.isPending}
                  onChange={handleZipPicked}
                />
                <Chip
                  clickable
                  icon={<FolderZipOutlinedIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
                  label={
                    !uploadZip.isPending
                      ? "Upload ZIP"
                      : uploadProgress?.phase === "committing"
                        ? "Committing…"
                        : uploadProgress?.files
                          ? `Uploading… ${uploadProgress.files} files`
                          : "Uploading ZIP…"
                  }
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#fff",
                    background: "rgba(255,255,255,0.22)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    pointerEvents: "none", // let the click reach the label
                  }}
                />
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Quick stats */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={cardSx}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 1,
            }}
          >
            Days Remaining
          </Typography>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 700,
              color: statusColor,
              lineHeight: 1,
            }}
          >
            {days === null
              ? "—"
              : days < 0
              ? `${Math.abs(days)}d`
              : `${days}d`}
          </Typography>
          <Typography
            sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}
          >
            {days === null
              ? "No expiry set"
              : days < 0
              ? "Expired"
              : "Until expiration"}
          </Typography>
        </Box>

        <Box sx={cardSx}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 1,
            }}
          >
            SSL Validity
          </Typography>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 700,
              color: colors.text,
              lineHeight: 1,
            }}
          >
            {domain.sslValidity ? `${domain.sslValidity}d` : "—"}
          </Typography>
          <Typography
            sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}
          >
            Certificate lifetime
          </Typography>
        </Box>

        <Box sx={cardSx}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 1,
            }}
          >
            Name Servers
          </Typography>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 700,
              color: colors.text,
              lineHeight: 1,
            }}
          >
            {(domain.nameServers || []).length}
          </Typography>
          <Typography
            sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}
          >
            Configured
          </Typography>
        </Box>
      </Box>

      {/* Detail sections grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        {/* Card 1: Client & Validity */}
        <Box
          sx={{
            ...cardSx,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SectionHeader
            icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Client Info & Validity"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />

          {/* Client sub-group */}
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "1px",
              mb: 1.5,
            }}
          >
            Client
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <InfoField
              label="Client Code"
              value={domain.clientCode}
              icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
              colors={colors}
              copyable
            />
            <InfoField
              label="Phone"
              value={domain.clientPhone}
              icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
              colors={colors}
              copyable
            />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <InfoField
                label="Email"
                value={domain.clientEmail}
                icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
                colors={colors}
                copyable
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: colors.divider, mb: 2 }} />

          {/* Validity sub-group */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 14, color: colors.textMuted }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: colors.textMuted,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Validity
            </Typography>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <InfoField
              label="Registered On"
              value={domain.registered}
              colors={colors}
            />
            <InfoField
              label="Expiry Date"
              value={domain.expiry}
              colors={colors}
            />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textFaint,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                }}
              >
                Status
              </Typography>
              <Chip
                size="small"
                label={
                  days === null
                    ? status
                    : days < 0
                    ? `Expired ${Math.abs(days)}d ago`
                    : `${days} days left`
                }
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: statusColor,
                  background: statusBg,
                  border: `1px solid ${statusColor}33`,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Card 2: Server & DNS */}
        <Box
          sx={{
            ...cardSx,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SectionHeader
            icon={<DnsOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Server & DNS"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />

          {/* Server sub-group */}
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "1px",
              mb: 1.5,
            }}
          >
            Server & Hosting
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <InfoField
              label="Server Name"
              value={domain.serverName}
              icon={<StorageOutlinedIcon sx={{ fontSize: 16 }} />}
              colors={colors}
              copyable
            />
            <InfoField
              label="IP Address"
              value={domain.ip}
              mono
              colors={colors}
              copyable
            />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <InfoField
                label="Registrar"
                value={domain.registrar}
                colors={colors}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: colors.divider, mb: 2 }} />

          {/* Name servers sub-group */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <VerifiedUserOutlinedIcon
              sx={{ fontSize: 14, color: colors.textMuted }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: colors.textMuted,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Name Servers
            </Typography>
          </Stack>
          {(domain.nameServers || []).length === 0 ? (
            <Typography
              sx={{
                fontSize: 13,
                color: colors.textFaint,
                fontStyle: "italic",
              }}
            >
              No name servers configured
            </Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                pr: 0.5,
                minWidth: 0,
              }}
            >
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                {domain.nameServers.map((ns, idx) => (
                  <Box
                    key={ns}
                    onClick={() => {
                      navigator.clipboard.writeText(ns);
                      toast.success("Copied to clipboard");
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 0.85,
                      borderRadius: 1.5,
                      background: "rgba(59, 130, 246, 0.08)",
                      border: "1px solid rgba(59, 130, 246, 0.25)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      minWidth: 0,
                      "&:hover": {
                        background: "rgba(59, 130, 246, 0.18)",
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: colors.textMuted,
                        background: colors.hover,
                        px: 0.75,
                        py: 0.15,
                        borderRadius: 0.75,
                        flexShrink: 0,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      NS{idx + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#3B82F6",
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontWeight: 500,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ns}
                    </Typography>
                    <ContentCopyOutlinedIcon
                      sx={{
                        fontSize: 14,
                        color: colors.textFaint,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Sub-Domains */}
      <Box sx={{ mt: 3, ...cardSx }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background:
                "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)",
              color: "#3B82F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LanOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 14,
                fontWeight: 700,
                color: colors.text,
                letterSpacing: "-0.2px",
                mt: "2px",
              }}
            >
              Sub-Domains
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
              Hosts under {domain.name} — own IP &amp; SSL, inherited registration
            </Typography>
          </Box>
          <Chip
            size="small"
            label={subdomains.length}
            sx={{
              fontWeight: 700,
              fontSize: 11,
              height: 22,
              background: colors.hover,
              color: colors.textMuted,
            }}
          />
          <Box sx={{ flex: 1 }} />
          {hasPermission("domains", "add") && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAddSubOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                px: 1.5,
                py: 0.4,
                minWidth: 0,
                borderRadius: 1.5,
                color: "#fff",
                background:
                  "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.35)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.45)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.15s ease",
              }}
            >
              Add Sub-Domain
            </Button>
          )}
        </Stack>
        <Divider sx={{ borderColor: colors.divider, mb: 2 }} />

        {subdomainsQuery.isLoading ? (
          <Typography sx={{ fontSize: 13, color: colors.textMuted, py: 2 }}>
            Loading…
          </Typography>
        ) : subdomains.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: colors.textMuted }}>
            <LanOutlinedIcon
              sx={{ fontSize: 36, color: colors.textFaint, mb: 1 }}
            />
            <Typography sx={{ fontSize: 13 }}>
              No sub-domains yet for this domain.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {subdomains.map((sub) => {
              const sslColor =
                sub.sslStatus === "valid"
                  ? "#16A34A"
                  : sub.sslStatus === "invalid"
                  ? "#DC2626"
                  : colors.textMuted;
              const statusColor =
                sub.status === "Active"
                  ? "#16A34A"
                  : sub.status === "Down"
                  ? "#DC2626"
                  : colors.textMuted;
              return (
                <Box
                  key={sub.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${colors.cardBorder}`,
                    background: colors.cardBg,
                  }}
                >
                  <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: colors.text,
                        wordBreak: "break-all",
                      }}
                    >
                      {sub.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.textMuted,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      {sub.ip || "No IP"}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 90 }}>
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                      SSL
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: sslColor }}>
                      {sub.sslValidity != null ? `${sub.sslValidity}d` : "—"}
                      {sub.sslStatus ? ` · ${sub.sslStatus}` : ""}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={sub.status || "Unknown"}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22,
                      color: statusColor,
                      background: `${statusColor}1f`,
                    }}
                  />
                  {hasPermission("domains", "delete") && (
                    <Tooltip title="Delete sub-domain">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSubdomain(sub)}
                        sx={{
                          color: colors.textMuted,
                          "&:hover": { color: "#DC2626", background: "rgba(239,68,68,0.1)" },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Activity Timeline */}
      <Box sx={{ mt: 3, ...cardSx }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                background:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)",
                color: "#3B82F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HistoryEduOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.text,
                  letterSpacing: "-0.2px",
                  mt: "2px",
                }}
              >
                Activity Timeline
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                Renewal reminders, client follow-ups, and other notes
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={`${domainActivity.length} entr${
              domainActivity.length === 1 ? "y" : "ies"
            }`}
            sx={{
              fontWeight: 700,
              fontSize: 11,
              height: 22,
              background: colors.hover,
              color: colors.textMuted,
            }}
          />
          <Box sx={{ flex: 1 }} />
          {hasPermission("logs", "add") && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAddActivityOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                px: 1.5,
                py: 0.4,
                minWidth: 0,
                borderRadius: 1.5,
                color: "#fff",
                background:
                  "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.35)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.45)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.15s ease",
              }}
            >
              Add Activity
            </Button>
          )}
        </Stack>
        <Divider sx={{ borderColor: colors.divider, mb: 3 }} />

        {domainActivity.length === 0 ? (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              color: colors.textMuted,
            }}
          >
            <HistoryEduOutlinedIcon
              sx={{ fontSize: 40, color: colors.textFaint, mb: 1 }}
            />
            <Typography
              sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}
            >
              No activity logged for this domain
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>
              Renewal reminders and client interactions will appear here
            </Typography>
          </Box>
        ) : (
          <Box>
            {activityDays.map((group) => (
              <Box key={group.date} sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
                {/* Day header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 1.5 }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {new Date(group.date).toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      background: colors.divider,
                    }}
                  />
                </Stack>

                {/* Day entries — vertical timeline. A continuous line runs
                    down the left edge; each entry hangs off a gradient node. */}
                <Box
                  sx={{
                    position: "relative",
                    pl: { xs: 4, md: 5 },
                    // The timeline spine.
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 16,
                      bottom: 16,
                      left: { xs: 15, md: 19 },
                      width: "2px",
                      background: colors.divider,
                    },
                  }}
                >
                  <Stack spacing={1.25}>
                    {group.items.map((entry) => (
                      <Box key={entry.id} sx={{ position: "relative" }}>
                        {/* Timeline node — sits on the spine */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: { xs: -29, md: -37 },
                            top: 12,
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            border: `3px solid ${colors.cardBg}`,
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.35)",
                            zIndex: 1,
                          }}
                        >
                          <HistoryEduOutlinedIcon sx={{ fontSize: 16 }} />
                        </Box>

                        {/* Entry card */}
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${colors.divider}`,
                            transition: "all 0.15s ease",
                            "&:hover": {
                              background: colors.hover,
                              borderColor: colors.cardBorder,
                            },
                          }}
                        >
                          {/* Top row: actor + timestamp */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 0.75 }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ color: colors.textFaint }}
                            >
                              <PersonOutlineOutlinedIcon
                                sx={{ fontSize: 12 }}
                              />
                              <Typography
                                sx={{ fontSize: 11, color: colors.textMuted }}
                              >
                                Admin
                              </Typography>
                            </Stack>

                            {/* Timestamp — clock time, tooltip = full date */}
                            <Tooltip title={formatAbsolute(entry.createdAt)}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: colors.textMuted,
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                {formatClock(entry.createdAt)} ·{" "}
                                {formatRelative(entry.createdAt)}
                              </Typography>
                            </Tooltip>
                          </Stack>

                          {/* Activity text */}
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: colors.text,
                              lineHeight: 1.45,
                              wordBreak: "break-word",
                            }}
                          >
                            {entry.activityText}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add Activity dialog */}
      <Dialog
        open={addActivityOpen}
        onClose={() => setAddActivityOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: HEADING_FONT,
            color: colors.text,
            fontWeight: 700,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          Add Activity — {domain.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.textMuted,
                lineHeight: 1.5,
              }}
            >
              Log a renewal reminder, client follow-up, or any other note
              for this domain. It will appear in this timeline and on the
              global Activity Log page.
            </Typography>
            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.text,
                  mb: 1,
                }}
              >
                Activity note
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                autoFocus
                placeholder="e.g. Called client about renewal; will follow up Tuesday"
                value={newActivityText}
                onChange={(e) => setNewActivityText(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: colors.cardBg,
                    color: colors.text,
                    borderRadius: 2,
                    "& fieldset": { borderColor: colors.cardBorder },
                    "&:hover fieldset": {
                      borderColor: colors.cardBorderHover,
                    },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                  },
                  "& textarea": { color: colors.text },
                  "& textarea::placeholder": {
                    color: colors.textFaint,
                    opacity: 1,
                  },
                }}
              />
            </Box>

            {/* Send a pre-typed reminder straight to the client */}
            <Box
              sx={{
                pt: 2,
                borderTop: `1px dashed ${colors.divider}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.5, width: "100%" }}
              >
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: colors.text }}
                >
                  Send reminder to client
                </Typography>
                <Typography
                  component="span"
                  onClick={() => navigate("/settings")}
                  sx={{
                    ml: "auto",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#3B82F6",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Edit templates
                </Typography>
              </Stack>
              <Typography
                sx={{ fontSize: 11.5, color: colors.textMuted, mb: 1.25 }}
              >
                Pre-filled from your saved template. Tweak if needed, then send —
                it also gets logged to the timeline.
              </Typography>

              {/* Channel toggle */}
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                {[
                  { key: "whatsapp", label: "WhatsApp", color: "#22c55e", icon: <WhatsAppIcon sx={{ fontSize: 17 }} /> },
                  { key: "email", label: "Email", color: "#2563eb", icon: <EmailOutlinedIcon sx={{ fontSize: 17 }} /> },
                ].map((ch) => {
                  const active = sendChannel === ch.key;
                  return (
                    <Button
                      key={ch.key}
                      onClick={() => setSendChannel(ch.key)}
                      startIcon={ch.icon}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        px: 2,
                        py: 0.6,
                        borderRadius: 2,
                        color: active ? "#fff" : colors.textMuted,
                        background: active ? ch.color : colors.hover,
                        border: `1px solid ${active ? ch.color : colors.cardBorder}`,
                        "&:hover": {
                          background: active ? ch.color : colors.hover,
                          borderColor: ch.color,
                        },
                      }}
                    >
                      {ch.label}
                    </Button>
                  );
                })}
              </Stack>

              {/* Email subject (email channel only) */}
              {sendChannel === "email" && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    component="label"
                    sx={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textMuted,
                      mb: 0.5,
                    }}
                  >
                    Subject
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={reminderSubject}
                    onChange={(e) => setReminderSubject(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: colors.cardBg,
                        color: colors.text,
                        borderRadius: 2,
                        "& fieldset": { borderColor: colors.cardBorder },
                        "&:hover fieldset": {
                          borderColor: colors.cardBorderHover,
                        },
                        "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                      },
                      "& input": { color: colors.text },
                    }}
                  />
                </Box>
              )}

              {/* Editable message preview */}
              <TextField
                fullWidth
                multiline
                minRows={7}
                value={reminderText}
                onChange={(e) => setReminderText(e.target.value)}
                placeholder={
                  templatesQuery.isLoading
                    ? "Loading template…"
                    : "Reminder message"
                }
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    background: colors.cardBg,
                    color: colors.text,
                    borderRadius: 2,
                    fontSize: 13.5,
                    "& fieldset": { borderColor: colors.cardBorder },
                    "&:hover fieldset": { borderColor: colors.cardBorderHover },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                  },
                  "& textarea": { color: colors.text },
                }}
              />

              {/* Send button for the selected channel */}
              {sendChannel === "whatsapp" ? (
                <Button
                  fullWidth
                  onClick={() => handleSendToClient("whatsapp")}
                  disabled={!reminderText.trim() || !domain.clientPhone}
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1,
                    borderRadius: 2,
                    color: "#fff",
                    background:
                      "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    boxShadow: "0 4px 14px rgba(34, 197, 94, 0.35)",
                    "&:hover": {
                      boxShadow: "0 8px 22px rgba(34, 197, 94, 0.45)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.6)",
                      background:
                        "linear-gradient(135deg, rgba(34,197,94,0.45) 0%, rgba(22,163,74,0.45) 100%)",
                    },
                  }}
                >
                  Send on WhatsApp
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={() => handleSendToClient("email")}
                  disabled={!reminderText.trim() || !domain.clientEmail}
                  startIcon={<EmailOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1,
                    borderRadius: 2,
                    color: "#fff",
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
                    "&:hover": {
                      boxShadow: "0 8px 22px rgba(37, 99, 235, 0.45)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.6)",
                      background:
                        "linear-gradient(135deg, rgba(37,99,235,0.45) 0%, rgba(29,78,216,0.45) 100%)",
                    },
                  }}
                >
                  Send Email
                </Button>
              )}

              {sendChannel === "whatsapp" && !domain.clientPhone && (
                <Typography sx={{ fontSize: 11, color: colors.textFaint, mt: 1 }}>
                  No client phone saved for this domain — add one to send on
                  WhatsApp.
                </Typography>
              )}
              {sendChannel === "email" && !domain.clientEmail && (
                <Typography sx={{ fontSize: 11, color: colors.textFaint, mt: 1 }}>
                  No client email saved for this domain — add one to send an
                  email.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}` }}
        >
          <Button
            onClick={() => {
              setAddActivityOpen(false);
              setNewActivityText("");
            }}
            sx={{
              textTransform: "none",
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddActivity}
            disabled={!newActivityText.trim()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background:
                "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.5)",
                background:
                  "linear-gradient(135deg, rgba(79,70,229,0.5) 0%, rgba(124,58,237,0.5) 100%)",
              },
            }}
          >
            Add Activity
          </Button>
        </DialogActions>
      </Dialog>

      <AddSubdomainModal
        open={addSubOpen}
        onClose={() => setAddSubOpen(false)}
        onSuccess={handleSubdomainAdded}
        placeholderName={`mail.${domain.name}`}
      />
    </DashboardLayout>
  );
}

export default DomainDetails;
